import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { setupAppPageBodyBackground } from "../game/gameStageBackground";
import {
  STAGE_ROW_KEY,
  ensureStageRowId,
  ensureStageSessionForUser,
  saveGameStageAndReport,
} from "../../functions/stageReportSync";
import StageTimer from "../game/StageTimer";
import {
  formatSecondsToClock,
  loadHallElapsedFromDb,
  persistHallElapsedTime,
  readTotalScoreFromStage,
  liveProgressPoints,
} from "../hall_1/hallTimer";
import { markHall2HotspotCompleted, getRedirectIfHall2StageDone } from "./heritageHallData";
import {
  MEMORY_ASSETS,
  MEMORY_WALL_POINTS,
  MEMORY_SUCCESS_COPY,
  MEMORY_PAIRS,
  buildMemoryDeck,
  markHallOfHeritageComplete,
  prepareEndGameData,
} from "./memoryWallData";
import deskBg from "./stage10/stage10_desk.jpg";
import mobBg from "./stage10/stage10_mob.jpg";
import letterBg from "../img/letter.png";
import greenBtn from "../img/green_button.png";
import "../fonts/breuer-headline.css";
import "../game/stageSuccessLetter.css";
import "./stage10.css";

const FLIP_BACK_MS = 780;

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

function MemoryTile({
  tile,
  faceUp,
  matched,
  shining,
  sealOpen,
  keyReady,
  disabled,
  onFlip,
}) {
  const isKey = tile.kind === "key";
  const showFront = isKey ? sealOpen : faceUp || matched;
  const keyBlocked = isKey && !keyReady && !sealOpen;

  return (
    <button
      type="button"
      className={`mem-tile${showFront ? " mem-tile--open" : ""}${
        matched ? " mem-tile--matched" : ""
      }${shining ? " mem-tile--shine" : ""}${isKey ? " mem-tile--key" : ""}${
        keyBlocked ? " mem-tile--key-waiting" : ""
      }${sealOpen && isKey ? " mem-tile--seal" : ""}${
        keyReady && isKey && !sealOpen ? " mem-tile--key-ready" : ""
      }`}
      disabled={disabled || matched || keyBlocked || (isKey && sealOpen)}
      onClick={() => onFlip?.(tile.index)}
      aria-label={
        isKey
          ? sealOpen
            ? "Heritage Key revealed"
            : keyReady
              ? "Flip to reveal the Heritage Key"
              : "Hidden tile"
          : showFront
            ? tile.kind === "name"
              ? tile.name
              : `${tile.name} image`
            : "Hidden tile"
      }
    >
      <span className="mem-tile__inner">
        <span
          className="mem-tile__face mem-tile__face--back"
          style={{ backgroundImage: `url(${MEMORY_ASSETS.back})` }}
        />
        <span
          className={`mem-tile__face mem-tile__face--front${
            tile.kind === "name" ? " mem-tile__face--name" : ""
          }`}
          style={
            tile.kind === "image"
              ? { backgroundImage: `url(${tile.image})` }
              : tile.kind === "key"
                ? { backgroundImage: `url(${MEMORY_ASSETS.locked})` }
                : { backgroundImage: `url(${MEMORY_ASSETS.front})` }
          }
        >
          {tile.kind === "name" ? (
            <span className="mem-tile__name">{tile.name}</span>
          ) : null}
          {shining ? <span className="mem-tile__shine" aria-hidden /> : null}
        </span>
      </span>
    </button>
  );
}

/** Hall 2 Stage 10 — Remember Me If You Can (5×5 memory wall). */
export default function HallStage10() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { user } = useSelector((s) => s.auth);
  const storedUser = useMemo(() => user || getStoredUser(), [user]);
  const isDemoBypass = storedUser?.sessionId === "demobypass";
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const deck = useMemo(() => buildMemoryDeck(), []);
  const [tiles] = useState(() =>
    deck.tiles.map((t, index) => ({ ...t, index }))
  );

  const [flipped, setFlipped] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState(() => new Set());
  const [shineIds, setShineIds] = useState(() => new Set());
  const [inputLocked, setInputLocked] = useState(false);
  const [sealOpen, setSealOpen] = useState(false);
  const [status, setStatus] = useState("playing");
  const [errorMsg, setErrorMsg] = useState("");
  const [elapsedSec, setElapsedSec] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [timerReady, setTimerReady] = useState(false);
  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || ""
  );

  const elapsedRef = useRef(0);
  const completingRef = useRef(false);
  const flipTimerRef = useRef(0);
  const shineTimerRef = useRef(0);
  const matchedRef = useRef(matchedPairs);

  useEffect(() => {
    matchedRef.current = matchedPairs;
  }, [matchedPairs]);

  const liveStagePoints = useMemo(
    () =>
      liveProgressPoints(
        matchedPairs.size,
        MEMORY_PAIRS.length,
        MEMORY_WALL_POINTS
      ),
    [matchedPairs]
  );

  const displayPoints =
    status === "success" ? totalScore : totalScore + liveStagePoints;

  useEffect(() => {
    dispatch(setBackButtonUrl("/hall-2"));
  }, [dispatch]);

  useEffect(() => {
    document.body.classList.add("hall2-stage10-page");
    const cleanupBg = setupAppPageBodyBackground(document.body, {
      deskForeground: deskBg,
      mobForeground: mobBg,
    });
    return () => {
      cleanupBg?.();
      document.body.classList.remove("hall2-stage10-page");
      if (flipTimerRef.current) window.clearTimeout(flipTimerRef.current);
      if (shineTimerRef.current) window.clearTimeout(shineTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      ensureStageSessionForUser(storedUser);
      const { stageId, elapsedSec: sec, stage } = await loadHallElapsedFromDb({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
      });
      if (cancelled) return;

      // Already finished stage10 (or further) — skip replay
      const redirect = getRedirectIfHall2StageDone(stage, 10);
      if (redirect) {
        navigate(redirect, {
          replace: true,
          state: {
            time: formatSecondsToClock(sec),
            elapsedSec: sec,
            points: Number(stage?.stage10) || MEMORY_WALL_POINTS,
          },
        });
        return;
      }

      if (stageId) setStageRowId(String(stageId));
      elapsedRef.current = sec;
      setElapsedSec(sec);
      setTotalScore(readTotalScoreFromStage(stage));
      setTimerReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, storedUser, isDemoBypass, navigate]);

  useEffect(() => {
    if (!timerReady || status === "success" || status === "saving") {
      return undefined;
    }
    const tick = window.setInterval(() => {
      setElapsedSec((s) => {
        const next = s + 1;
        elapsedRef.current = next;
        return next;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [timerReady, status]);

  useEffect(() => {
    if (!timerReady) return undefined;
    const save = () => {
      persistHallElapsedTime({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId: stageRowId || sessionStorage.getItem(STAGE_ROW_KEY),
        elapsedSec: elapsedRef.current,
      });
    };
    const id = window.setInterval(save, 15000);
    return () => {
      clearInterval(id);
      if (status === "playing") save();
    };
  }, [
    timerReady,
    backendBase,
    adminToken,
    storedUser,
    isDemoBypass,
    stageRowId,
    status,
  ]);

  const completePuzzle = useCallback(async () => {
    if (completingRef.current || status === "success" || status === "saving") {
      return;
    }
    completingRef.current = true;
    setStatus("saving");
    setErrorMsg("");

    const timeStr = formatSecondsToClock(elapsedRef.current);
    const answers = {
      puzzle: "remember_me_if_you_can",
      pairsMatched: 12,
      seal: true,
    };

    try {
      let stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
      if (!stageId && !isDemoBypass && backendBase && adminToken) {
        stageId = await ensureStageRowId({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
        });
        if (stageId) setStageRowId(String(stageId));
      }

      if (!isDemoBypass && backendBase && adminToken && stageId) {
        const saveResult = await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          stageNumber: 10,
          score: MEMORY_WALL_POINTS,
          answers,
          time: timeStr,
        });
        if (!saveResult?.ok) {
          throw new Error("stage10 save failed");
        }
        if (saveResult.total_score != null) {
          setTotalScore(Number(saveResult.total_score) || 0);
        } else {
          setTotalScore((prev) => prev + MEMORY_WALL_POINTS);
        }
        // Mirror DB gameover locally so login / resume treat this run as finished
        try {
          const raw = sessionStorage.getItem("userData");
          if (raw) {
            const u = JSON.parse(raw);
            u.gameover = 1;
            sessionStorage.setItem("userData", JSON.stringify(u));
          }
        } catch {
          /* ignore */
        }
      } else {
        setTotalScore((prev) => prev + MEMORY_WALL_POINTS);
      }

      markHall2HotspotCompleted("remember_me_if_you_can");
      markHallOfHeritageComplete();
      const endData = prepareEndGameData({
        elapsedSec: elapsedRef.current,
        stage10Score: MEMORY_WALL_POINTS,
      });
      persistHallElapsedTime({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId: stageId || sessionStorage.getItem(STAGE_ROW_KEY),
        elapsedSec: elapsedRef.current,
      });
      setStatus("success");
      return endData;
    } catch (err) {
      console.error("Memory wall save:", err);
      completingRef.current = false;
      setStatus("playing");
      setErrorMsg("Could not save progress. Please finish the pairs again.");
      return null;
    }
  }, [
    status,
    stageRowId,
    isDemoBypass,
    backendBase,
    adminToken,
    storedUser,
  ]);

  const allPairsDone = matchedPairs.size >= 12;

  const revealKeyTile = useCallback(() => {
    if (sealOpen || completingRef.current) return;
    setSealOpen(true);
    setInputLocked(true);
    completePuzzle();
  }, [sealOpen, completePuzzle]);

  const handleFlip = useCallback(
    (index) => {
      if (status !== "playing") return;
      const tile = tiles[index];
      if (!tile) return;

      // Key tile: only clickable after all 12 pairs — player must flip it manually
      if (tile.kind === "key") {
        if (matchedRef.current.size < 12 || sealOpen || inputLocked) return;
        revealKeyTile();
        return;
      }

      if (inputLocked) return;
      if (matchedRef.current.has(tile.pairId)) return;
      if (flipped.includes(index)) return;
      if (flipped.length >= 2) return;

      const next = [...flipped, index];
      setFlipped(next);

      if (next.length < 2) return;

      const a = tiles[next[0]];
      const b = tiles[next[1]];
      const isMatch =
        a.pairId && a.pairId === b.pairId && a.kind !== b.kind;

      if (isMatch) {
        const nextMatched = new Set(matchedRef.current);
        nextMatched.add(a.pairId);
        matchedRef.current = nextMatched;
        setMatchedPairs(nextMatched);
        setShineIds(new Set([a.index, b.index]));
        if (shineTimerRef.current) window.clearTimeout(shineTimerRef.current);
        shineTimerRef.current = window.setTimeout(() => {
          setShineIds(new Set());
        }, 900);
        setFlipped([]);
        return;
      }

      setInputLocked(true);
      flipTimerRef.current = window.setTimeout(() => {
        setFlipped([]);
        setInputLocked(false);
      }, FLIP_BACK_MS);
    },
    [flipped, inputLocked, status, tiles, sealOpen, revealKeyTile]
  );

  const matchedCount = matchedPairs.size;

  return (
    <div
      className={`hall2-stage10 memory-play${
        status === "success" ? " memory-play--success" : ""
      }`}
    >
      <div className="stage-escape-hud">
        <StageTimer
          timeLabel={formatSecondsToClock(elapsedSec)}
          points={displayPoints}
        />
      </div>

      <div className="memory-play__inner">
        <header className="memory-play__header">
          <h1 className="memory-play__title"> REMEMBER ME IF YOU CAN          </h1>
          <div className="memory-play__rule" aria-hidden>
            <span />
          </div>
          <p className="memory-play__hint">
          Match each artifact to its name to restore the artifact wall.
          </p>
        </header>

        <div className="memory-play__layout">
          <div className="memory-play__board" role="grid" aria-label="Memory wall">
            {tiles.map((tile) => {
              const isFaceUp = flipped.includes(tile.index);
              const isMatched =
                tile.pairId != null && matchedPairs.has(tile.pairId);
              return (
                <MemoryTile
                  key={tile.id}
                  tile={tile}
                  faceUp={isFaceUp}
                  matched={isMatched}
                  shining={shineIds.has(tile.index)}
                  sealOpen={tile.kind === "key" && sealOpen}
                  keyReady={tile.kind === "key" && allPairsDone && !sealOpen}
                  disabled={
                    status !== "playing" ||
                    (inputLocked && tile.kind !== "key")
                  }
                  onFlip={handleFlip}
                />
              );
            })}
          </div>

          {status === "success" || sealOpen ? (
            <div
              className="memory-play__success memory-play__success--in"
              role="dialog"
              aria-modal="true"
              aria-labelledby="memory-success-title"
            >
              <div className="memory-play__success-veil" aria-hidden />
              <aside
                className="memory-play__success-card stage-success-letter"
                style={{ backgroundImage: `url(${letterBg})` }}
              >
                <h2
                  id="memory-success-title"
                  className="memory-play__success-kicker"
                >
                  HERITAGE SEAL UNLOCKED!
                </h2>
                <div className="memory-play__seal-wrap">
                  <img
                    src={MEMORY_ASSETS.seal}
                    alt="Heritage Seal"
                    className="memory-play__seal"
                    draggable={false}
                  />
                </div>
                <div
                  className="memory-play__rule memory-play__success-rule"
                  aria-hidden
                >
                  <span />
                </div>
                <p className="memory-play__success-caption">
                  {MEMORY_SUCCESS_COPY.sealCaption}
                </p>
                {status === "success" ? (
                  <button
                    type="button"
                    className="memory-play__cta"
                    style={{ backgroundImage: `url(${greenBtn})` }}
                    onClick={() =>
                      navigate("/complete", {
                        state: {
                          time: formatSecondsToClock(elapsedSec),
                          elapsedSec,
                          points: MEMORY_WALL_POINTS,
                        },
                      })
                    }
                  >
                    CONTINUE
                  </button>
                ) : status === "saving" ? (
                  <p className="memory-play__saving" aria-live="polite">
                    Restoring the Heritage Seal…
                  </p>
                ) : null}
              </aside>
            </div>
          ) : null}
        </div>

        {errorMsg ? (
          <p className="memory-play__error" role="alert">
            {errorMsg}
          </p>
        ) : null}
      </div>
    </div>
  );
}
